import { canonicalize } from './canonicalize';

export const FEATURES_REFRESH_TIMESTAMP_WINDOW_SECONDS = 300;
export const FEATURES_REFRESH_NONCE_CACHE_SIZE = 1000;

export type FeaturesRefreshRejectionReason = 'missingHeader' | 'staleTimestamp' | 'invalidSignature' | 'replay';

export interface FeaturesRefreshSignatureInput {
  body: unknown;
  timestamp: string;
  nonce: string;
}

export interface FeaturesRefreshVerificationInput extends FeaturesRefreshSignatureInput {
  signature: string;
}

export type FeaturesRefreshVerification = { valid: true } | { valid: false; reason: FeaturesRefreshRejectionReason };

export interface FeaturesRefreshNonceCache {
  has(nonce: string): boolean;
  add(nonce: string, expiresAt: number): void;
}

export class LruFeaturesRefreshNonceCache implements FeaturesRefreshNonceCache {
  private readonly entries = new Map<string, number>();

  constructor(private readonly maxSize = FEATURES_REFRESH_NONCE_CACHE_SIZE) {}

  has(nonce: string): boolean {
    this.removeExpired();
    const expiresAt = this.entries.get(nonce);
    if (expiresAt === undefined) return false;
    this.entries.delete(nonce);
    this.entries.set(nonce, expiresAt);
    return true;
  }

  add(nonce: string, expiresAt: number): void {
    this.removeExpired();
    this.entries.delete(nonce);
    this.entries.set(nonce, expiresAt);
    while (this.entries.size > this.maxSize) {
      const oldest = this.entries.keys().next().value;
      if (oldest === undefined) return;
      this.entries.delete(oldest);
    }
  }

  private removeExpired(): void {
    const now = Date.now();
    for (const [nonce, expiresAt] of this.entries) {
      if (expiresAt <= now) this.entries.delete(nonce);
    }
  }
}

export async function signFeaturesRefreshBody(secret: string, input: FeaturesRefreshSignatureInput): Promise<string> {
  const key = await globalThis.crypto.subtle.importKey('raw', new TextEncoder().encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, [
    'sign',
  ]);
  const signature = await globalThis.crypto.subtle.sign('HMAC', key, new TextEncoder().encode(canonicalizeSignatureInput(input)));
  return [...new Uint8Array(signature)].map(byte => byte.toString(16).padStart(2, '0')).join('');
}

export async function verifyFeaturesRefreshBody(
  secrets: readonly string[],
  input: { body: unknown; signature: string | undefined; timestamp: string | undefined; nonce: string | undefined },
  nonceCache: FeaturesRefreshNonceCache,
  nowSeconds = Math.floor(Date.now() / 1000)
): Promise<FeaturesRefreshVerification> {
  const { body, signature, timestamp, nonce } = input;
  if (
    secrets.length === 0 ||
    typeof signature !== 'string' ||
    typeof timestamp !== 'string' ||
    typeof nonce !== 'string' ||
    nonce.length === 0
  ) {
    return { valid: false, reason: 'missingHeader' };
  }
  if (!/^[a-f0-9]{64}$/.test(signature)) {
    return { valid: false, reason: 'invalidSignature' };
  }
  const timestampSeconds = Number(timestamp);
  if (!Number.isInteger(timestampSeconds) || Math.abs(nowSeconds - timestampSeconds) > FEATURES_REFRESH_TIMESTAMP_WINDOW_SECONDS) {
    return { valid: false, reason: 'staleTimestamp' };
  }

  const signed = { body, timestamp, nonce };
  const candidates = await Promise.all(secrets.map(secret => signFeaturesRefreshBody(secret, signed)));
  if (!candidates.some(expected => constantTimeEquals(expected, signature))) {
    return { valid: false, reason: 'invalidSignature' };
  }
  if (nonceCache.has(nonce)) {
    return { valid: false, reason: 'replay' };
  }

  nonceCache.add(nonce, Date.now() + FEATURES_REFRESH_TIMESTAMP_WINDOW_SECONDS * 1000);
  return { valid: true };
}

function canonicalizeSignatureInput(input: FeaturesRefreshSignatureInput): string {
  return canonicalize({ body: input.body, nonce: input.nonce, timestamp: input.timestamp });
}

function constantTimeEquals(expected: string, provided: string): boolean {
  if (expected.length !== provided.length) return false;
  let difference = 0;
  for (let index = 0; index < expected.length; index++) {
    difference |= (expected.codePointAt(index) ?? 0) ^ (provided.codePointAt(index) ?? 0);
  }
  return difference === 0;
}
